import { useState, useEffect } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  FileText,
  Webhook,
  Activity,
  RefreshCw,
  Search,
  Download,
  Eye,
  MoreHorizontal,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Globe,
  Server,
  Cpu,
  HardDrive,
  Wifi,
  Shield,
  Settings,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { apiClient } from '@/services/api'

// Types for API responses
interface SystemHealth {
  id: number
  status: 'healthy' | 'warning' | 'critical' | 'offline'
  uptime_percentage: string
  response_time_ms: number
  cpu_usage_percentage: number
  memory_usage_percentage: number
  disk_usage_percentage: number
  active_connections: number
  is_healthy: boolean
  needs_attention: boolean
  last_updated: string
  last_updated_ago: string
  created_at: string
}

interface AuditLog {
  id: number
  user_email: string
  user_name: string
  action: string
  status: 'success' | 'failed' | 'pending' | 'cancelled'
  details: string
  ip_address: string
  created_at: string
  time_ago: string
}

interface WebhookConfig {
  id: number
  name: string
  url: string
  event_type: string
  status: 'active' | 'inactive' | 'paused'
  success_count: number
  failure_count: number
  success_rate: number
  is_active: boolean
  last_triggered: string | null
  last_triggered_ago: string | null
  created_at: string
}

export default function System() {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [activeTab, setActiveTab] = useState('audit-logs')
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([])
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState<string | null>(null)

  const tabs = [
    { id: 'audit-logs', label: 'Audit Logs', icon: FileText },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'health', label: 'System Health', icon: Activity },
  ]

  // API functions
  const fetchSystemHealth = async () => {
    try {
      const response = (await apiClient.get('/system/health/overview/')) as any
      console.log('System health response:', response.data) // Debug log

      if (response.data && response.data.success && response.data.data) {
        setSystemHealth(response.data.data)
      } else if (response.data && !response.data.success) {
        // Direct data response (no success wrapper)
        setSystemHealth(response.data)
      } else {
        console.warn('System health response missing data:', response.data)
        setSystemHealth(null)
      }
    } catch (err) {
      console.error('Error fetching system health:', err)
      setError('Failed to fetch system health data')
    }
  }

  const fetchAuditLogs = async () => {
    try {
      const response = (await apiClient.get('/system/audit-logs/')) as any
      console.log('Audit logs response:', response.data) // Debug log

      // Handle different response structures
      if (response.data && response.data.results) {
        // Paginated response
        setAuditLogs(response.data.results)
      } else if (response.data && Array.isArray(response.data)) {
        // Direct array response
        setAuditLogs(response.data)
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Wrapped in data object
        setAuditLogs(response.data.data)
      } else {
        console.warn('Unexpected audit logs response structure:', response.data)
        setAuditLogs([])
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err)
      setError('Failed to fetch audit logs')
    }
  }

  const fetchWebhooks = async () => {
    try {
      const response = (await apiClient.get('/system/webhook-configs/')) as any
      console.log('Webhooks response:', response.data) // Debug log

      // Handle different response structures
      if (response.data && response.data.results) {
        // Paginated response
        setWebhooks(response.data.results)
      } else if (response.data && Array.isArray(response.data)) {
        // Direct array response
        setWebhooks(response.data)
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // Wrapped in data object
        setWebhooks(response.data.data)
      } else {
        console.warn('Unexpected webhooks response structure:', response.data)
        setWebhooks([])
      }
    } catch (err) {
      console.error('Error fetching webhooks:', err)
      setError('Failed to fetch webhook configurations')
    }
  }

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      setError(null)

      try {
        await Promise.all([fetchSystemHealth(), fetchAuditLogs(), fetchWebhooks()])
      } catch (err) {
        console.error('Error loading system data:', err)
        setError('Failed to load system data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'success':
        return { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle }
      case 'failed':
        return { color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle }
      case 'active':
        return { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle }
      case 'inactive':
        return { color: 'text-gray-600 bg-gray-50 border-gray-200', icon: XCircle }
      case 'healthy':
        return { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle }
      case 'warning':
        return { color: 'text-yellow-600 bg-yellow-50 border-yellow-200', icon: AlertCircle }
      default:
        return { color: 'text-gray-600 bg-gray-50 border-gray-200', icon: Clock }
    }
  }

  const refreshData = async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchSystemHealth(), fetchAuditLogs(), fetchWebhooks()])
    } catch (err) {
      console.error('Error refreshing data:', err)
      setError('Failed to refresh data')
    } finally {
      setLoading(false)
    }
  }

  const filteredAuditLogs = auditLogs.filter(
    log =>
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const filteredWebhooks = webhooks.filter(
    webhook =>
      webhook.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.event_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      webhook.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div
      className="min-h-screen"
      style={pageStyles.container}
    >
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-3xl font-bold"
                style={pageStyles.text}
              >
                System Management
              </h1>
              <p
                className="mt-2"
                style={pageStyles.textSecondary}
              >
                Monitor system health, audit logs, and webhook configurations
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={refreshData}
                disabled={loading}
                className="text-white bg-gray-800/50 border-gray-700/50 hover:bg-gray-700/50"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white"
                style={{
                  backgroundColor: `${branding.primaryColor}20`,
                  borderColor: `${branding.textSecondaryColor}30`,
                  color: branding.textColor,
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div
            className="mb-6 p-6 transition-all duration-300 border card backdrop-blur-sm"
            style={{
              ...cardStyles,
              borderColor: statusColors.error.borderColor
            }}
          >
            <div className="flex items-start space-x-4">
              <div
                className="p-3 rounded-xl"
                style={{
                  backgroundColor: statusColors.error.backgroundColor
                }}
              >
                <AlertCircle
                  className="w-6 h-6"
                  style={{ color: statusColors.error.color }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h3
                  className="mb-1 text-lg font-semibold"
                  style={pageStyles.text}
                >
                  Error loading system data
                </h3>
                <p
                  className="mb-4 text-sm"
                  style={{ color: statusColors.error.color }}
                >
                  {error}
                </p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={refreshData}
                    style={{
                      backgroundColor: `${statusColors.error.color}20`,
                      borderColor: statusColors.error.borderColor,
                      color: statusColors.error.color
                    }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    style={{
                      color: statusColors.error.color
                    }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System Health Overview */}
        {systemHealth && (
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
            <div
              className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
              style={{
                ...cardStyles,
                borderColor: statusColors.success.borderColor
              }}
            >
              <div className="p-6 card-content">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase"
                      style={pageStyles.textSecondary}
                    >
                      System Status
                    </p>
                    <p className="mt-2 text-3xl font-bold text-white capitalize"
                      style={pageStyles.text}
                    >
                      {systemHealth.status}
                    </p>
                    <p className="mt-1 text-xs text-gray-400"
                      style={pageStyles.textSecondary}
                    >
                      Current system status
                    </p>
                  </div>
                  <div
                    className={`p-4 shadow-lg rounded-2xl ${systemHealth.is_healthy ? 'bg-green-500' : 'bg-red-500'
                      }`}
                    style={{
                      backgroundColor: systemHealth.is_healthy ? statusColors.success.backgroundColor : statusColors.error.backgroundColor
                    }}
                  >
                    <Server className="w-8 h-8"
                      style={{ color: systemHealth.is_healthy ? statusColors.success.color : statusColors.error.color }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
              style={{
                ...cardStyles,
                borderColor: statusColors.success.borderColor
              }}
            >
              <div className="p-6 card-content">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase"
                      style={pageStyles.textSecondary}
                    >
                      Uptime
                    </p>
                    <p className="mt-2 text-3xl font-bold text-white"
                      style={pageStyles.text}
                    >
                      {systemHealth.uptime_percentage}%
                    </p>
                    <p className="mt-1 text-xs text-gray-400"
                      style={pageStyles.textSecondary}
                    >
                      System availability
                    </p>
                  </div>
                  <div className="p-4 bg-blue-500 shadow-lg rounded-2xl"
                    style={{
                      backgroundColor: statusColors.success.backgroundColor
                    }}
                  >
                    <Clock className="w-8 h-8"
                      style={{ color: statusColors.success.color }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50"
              style={{
                ...cardStyles,
                borderColor: statusColors.success.borderColor
              }}
            >
              <div className="p-6 card-content">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase"
                      style={pageStyles.textSecondary}
                    >
                      Response Time
                    </p>
                    <p className="mt-2 text-3xl font-bold text-white"
                      style={pageStyles.text}
                    >
                      {systemHealth.response_time_ms}ms
                    </p>
                    <p className="mt-1 text-xs text-gray-400"
                      style={pageStyles.textSecondary}
                    >
                      Average response time
                    </p>
                  </div>
                  <div className="p-4 bg-purple-500 shadow-lg rounded-2xl"
                    style={{
                      backgroundColor: statusColors.success.backgroundColor
                    }}
                  >
                    <Activity className="w-8 h-8"
                      style={{ color: statusColors.success.color }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50"
              style={{
                ...cardStyles,
                borderColor: statusColors.success.borderColor
              }}
            >
              <div className="p-6 card-content">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="text-sm font-semibold tracking-wide text-gray-300 uppercase"
                      style={pageStyles.textSecondary}
                    >
                      Active Connections
                    </p>
                    <p className="mt-2 text-3xl font-bold text-white"
                      style={pageStyles.text}
                    >
                      {systemHealth.active_connections.toLocaleString()}
                    </p>
                    <p className="mt-1 text-xs text-gray-400"
                      style={pageStyles.textSecondary}
                    >
                      Current connections
                    </p>
                  </div>
                  <div className="p-4 bg-orange-500 shadow-lg rounded-2xl"
                    style={{
                      backgroundColor: statusColors.success.backgroundColor
                    }}
                  >
                    <Wifi className="w-8 h-8"
                      style={{ color: statusColors.success.color }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm mb-6"
          style={{
            ...cardStyles,
            borderColor: statusColors.success.borderColor
          }}
        >
          <div className="card-content p-0">
            <div className="border-b"
              style={{
                borderColor: statusColors.success.borderColor
              }}
            >
              <nav className="flex space-x-8 px-6">
                {tabs.map(tab => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === tab.id
                        ? `border-${branding.primaryColor} text-${branding.primaryColor}`
                        : `border-transparent text-${branding.textSecondaryColor} hover:text-${branding.primaryColor} hover:border-${branding.primaryColor}`
                        }`}
                      style={{
                        borderColor: activeTab === tab.id ? branding.primaryColor : 'transparent',
                        color: activeTab === tab.id ? branding.primaryColor : branding.textSecondaryColor
                      }}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'audit-logs' && (
          <div className="space-y-6"
            style={{
              ...cardStyles,
              borderColor: statusColors.success.borderColor
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin"
                  style={{ color: branding.textSecondaryColor }}
                />
                <span className="ml-2"
                  style={{ color: branding.textSecondaryColor }}
                >
                  Loading audit logs...
                </span>
              </div>
            ) : filteredAuditLogs.length === 0 ? (
              <div className="flex items-center justify-center py-12"
                style={{
                  ...cardStyles,
                  borderColor: statusColors.success.borderColor
                }}
              >
                <FileText className="h-12 w-12"
                  style={{ color: branding.textSecondaryColor }}
                />
                <div className="ml-4 text-center">
                  <h3 className="text-lg font-medium text-white"
                    style={pageStyles.text}
                  >
                    No audit logs found
                  </h3>
                  <p className="text-gray-400"
                    style={pageStyles.textSecondary}
                  >
                    No audit logs match your current search criteria.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Search and Filters */}
                <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
                  style={{
                    ...cardStyles,
                    borderColor: statusColors.success.borderColor
                  }}
                >
                  <div className="card-content">
                    <div className="flex flex-col lg:flex-row gap-4">
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4"
                            style={{ color: branding.textSecondaryColor }}
                          />
                          <input
                            type="text"
                            placeholder="Search audit logs..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="input pl-10"
                            style={{
                              backgroundColor: `${branding.surfaceColor}80`,
                              borderColor: `${branding.textSecondaryColor}30`,
                              color: branding.textColor,
                            }}
                          />
                        </div>
                      </div>

                      <select className="input"
                        style={{
                          backgroundColor: `${branding.surfaceColor}80`,
                          borderColor: `${branding.textSecondaryColor}30`,
                          color: branding.textColor,
                        }}
                      >
                        <option value="">All Actions</option>
                        <option value="login">Login</option>
                        <option value="transaction">Transaction</option>
                        <option value="api">API</option>
                      </select>

                      <select className="input"
                        style={{
                          backgroundColor: `${branding.surfaceColor}80`,
                          borderColor: `${branding.textSecondaryColor}30`,
                          color: branding.textColor,
                        }}
                      >
                        <option value="">All Status</option>
                        <option value="success">Success</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Audit Logs Table */}
                <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm"
                  style={{
                    ...cardStyles,
                    borderColor: statusColors.success.borderColor
                  }}
                >
                  <div className="card-content p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b"
                          style={{
                            borderColor: statusColors.success.borderColor
                          }}
                        >
                          <tr>
                            <th className="text-left py-4 px-6 font-semibold text-white"
                              style={pageStyles.textSecondary}
                            >Action</th>
                            <th className="text-left py-4 px-6 font-semibold text-white"
                              style={pageStyles.textSecondary}
                            >User</th>
                            <th className="text-left py-4 px-6 font-semibold text-white">
                              IP Address
                            </th>
                            <th className="text-left py-4 px-6 font-semibold text-white"
                              style={pageStyles.textSecondary}
                            >Status</th>
                            <th className="text-left py-4 px-6 font-semibold text-white"
                              style={pageStyles.textSecondary}
                            >
                              Timestamp
                            </th>
                            <th className="text-left py-4 px-6 font-semibold text-white"
                              style={pageStyles.textSecondary}
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAuditLogs.map(log => {
                            const statusConfig = getStatusConfig(log.status)
                            const StatusIcon = statusConfig.icon

                            return (
                              <tr
                                key={log.id}
                                className="border-b border-gray-700/30 hover:bg-gray-800/50"
                                style={{
                                  borderColor: statusColors.success.borderColor
                                }}
                              >
                                <td className="py-4 px-6">
                                  <div>
                                    <p className="font-medium text-white"
                                      style={pageStyles.text}
                                    >{log.action}</p>
                                    <p className="text-sm text-gray-400"
                                      style={pageStyles.textSecondary}
                                    >{log.details}</p>
                                  </div>
                                </td>
                                <td className="py-4 px-6">
                                  <p className="text-sm text-white"
                                    style={pageStyles.text}
                                  >{log.user_name}</p>
                                  <p className="text-xs text-gray-400"
                                    style={pageStyles.textSecondary}
                                  >{log.user_email}</p>
                                </td>
                                <td className="py-4 px-6">
                                  <p className="text-sm text-white"
                                    style={pageStyles.text}
                                  >{log.ip_address}</p>
                                </td>
                                <td className="py-4 px-6">
                                  <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}
                                    style={{
                                      borderColor: statusConfig.color,
                                      color: statusConfig.color
                                    }}
                                  >
                                    <StatusIcon className="h-3 w-3 mr-1" />
                                    {log.status}
                                  </span>
                                </td>
                                <td className="py-4 px-6">
                                  <div>
                                    <p className="text-sm text-white"
                                      style={pageStyles.text}
                                    >
                                      {new Date(log.created_at).toLocaleDateString()}
                                    </p>
                                    <p className="text-xs text-gray-400"
                                      style={pageStyles.textSecondary}
                                    >{log.time_ago}</p>
                                  </div>
                                </td>
                                <td className="py-4 px-6">
                                  <div className="flex items-center space-x-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700/50"
                                      style={{
                                        backgroundColor: `${branding.surfaceColor}80`,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor,
                                      }}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700/50"
                                    >
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'webhooks' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-400">Loading webhooks...</span>
              </div>
            ) : filteredWebhooks.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Webhook className="h-12 w-12 text-gray-400" />
                <div className="ml-4 text-center">
                  <h3 className="text-lg font-medium text-white">No webhooks found</h3>
                  <p className="text-gray-400">
                    No webhook configurations match your current search criteria.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* Webhooks Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredWebhooks.map(webhook => {
                    const statusConfig = getStatusConfig(webhook.status)
                    const StatusIcon = statusConfig.icon

                    return (
                      <div
                        key={webhook.id}
                        className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50"
                      >
                        <div className="card-content">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-500/20 rounded-lg">
                                <Webhook className="h-5 w-5 text-blue-400" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-white text-sm">
                                  Webhook #{webhook.id}
                                </h3>
                                <p className="text-xs text-gray-400">{webhook.event_type}</p>
                              </div>
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${statusConfig.color}`}
                            >
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {webhook.status}
                            </span>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="flex items-center text-sm text-gray-300">
                              <Globe className="h-4 w-4 mr-2 text-gray-400" />
                              <span className="truncate">{webhook.url}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-300">
                              <Clock className="h-4 w-4 mr-2 text-gray-400" />
                              <span>
                                {webhook.last_triggered_ago
                                  ? `Last triggered ${webhook.last_triggered_ago}`
                                  : 'Never triggered'}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="text-center">
                              <p className="text-lg font-semibold text-green-400">
                                {webhook.success_count}
                              </p>
                              <p className="text-xs text-gray-400">Success</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-semibold text-red-400">
                                {webhook.failure_count}
                              </p>
                              <p className="text-xs text-gray-400">Failed</p>
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-4 border-t border-gray-700/50">
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-3 text-xs text-gray-400 hover:text-white hover:bg-gray-700/50"
                              >
                                <Settings className="h-3 w-3 mr-1" />
                                Configure
                              </Button>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700/50"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-gray-400 hover:text-white hover:bg-gray-700/50"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'health' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                <span className="ml-2 text-gray-400">Loading system health data...</span>
              </div>
            ) : systemHealth ? (
              <>
                {/* System Resources */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                    <div className="card-content">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-white">CPU Usage</h3>
                        <Cpu className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Usage</span>
                          <span className="font-medium text-white">
                            {systemHealth.cpu_usage_percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-700/50 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-red-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${systemHealth.cpu_usage_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                    <div className="card-content">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-white">Memory Usage</h3>
                        <HardDrive className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Usage</span>
                          <span className="font-medium text-white">
                            {systemHealth.memory_usage_percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-700/50 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${systemHealth.memory_usage_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                    <div className="card-content">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-white">Disk Usage</h3>
                        <HardDrive className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Usage</span>
                          <span className="font-medium text-white">
                            {systemHealth.disk_usage_percentage}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-700/50 rounded-full h-2">
                          <div
                            className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${systemHealth.disk_usage_percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Information */}
                <div className="transition-all duration-300 border card hover:shadow-xl bg-slate-800/80 backdrop-blur-sm border-gray-700/50">
                  <div className="card-header">
                    <h3 className="card-title text-xl text-orange-400">System Information</h3>
                    <p className="card-description">Current system status and configuration</p>
                  </div>
                  <div className="card-content">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Server Status</span>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusConfig(systemHealth.status).color}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {systemHealth.status}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Uptime</span>
                          <span className="text-sm font-medium text-white">
                            {systemHealth.uptime_percentage}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Response Time</span>
                          <span className="text-sm font-medium text-white">
                            {systemHealth.response_time_ms}ms
                          </span>
                        </div>
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Active Connections</span>
                          <span className="text-sm font-medium text-white">
                            {systemHealth.active_connections.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Last Updated</span>
                          <span className="text-sm font-medium text-white">
                            {systemHealth.last_updated_ago}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-400">Security Status</span>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-green-400 bg-green-500/20 border border-green-500/30">
                            <Shield className="h-3 w-3 mr-1" />
                            Secure
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-12">
                <AlertCircle className="h-8 w-8 text-gray-400" />
                <span className="ml-2 text-gray-400">No system health data available</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
